import {
  Directive,
  Input,
  TemplateRef,
  ViewContainerRef,
  effect,
  inject,
  signal,
} from '@angular/core';

import type { Permission } from './permissions';
import { PermissionService } from './permission.service';

/**
 * Directiva estructural `*appHasPermission`.
 *
 * Renderiza el bloque sólo si el usuario tiene el permiso. Reactiva vía Signals:
 * si los permisos cambian (login/renew/switch de tenant), el DOM se actualiza.
 *
 *   <button *appHasPermission="'documents.write'">Cargar documento</button>
 *   <a *appHasPermission="['mdvr.read','mdvr.download']; mode: 'any'">Video</a>
 */
@Directive({
  selector: '[appHasPermission]',
  standalone: true,
})
export class HasPermissionDirective {
  private readonly permissions = inject(PermissionService);
  private readonly templateRef = inject(TemplateRef<unknown>);
  private readonly viewContainer = inject(ViewContainerRef);

  private readonly required = signal<readonly (Permission | string)[]>([]);
  private readonly mode = signal<'all' | 'any'>('all');
  private hasView = false;

  @Input({ required: true })
  set appHasPermission(value: Permission | string | readonly (Permission | string)[]) {
    this.required.set(Array.isArray(value) ? value : [value as Permission]);
  }

  /** `*appHasPermission="perms; mode: 'any'"` — default 'all'. */
  @Input()
  set appHasPermissionMode(mode: 'all' | 'any') {
    this.mode.set(mode);
  }

  constructor() {
    effect(() => {
      const perms = this.required();
      const allowed =
        this.mode() === 'any'
          ? this.permissions.hasAny(perms)
          : this.permissions.hasAll(perms);

      if (allowed && !this.hasView) {
        this.viewContainer.createEmbeddedView(this.templateRef);
        this.hasView = true;
      } else if (!allowed && this.hasView) {
        this.viewContainer.clear();
        this.hasView = false;
      }
    });
  }
}
